import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Order } from '../models';
import { API_ENDPOINTS } from '../config/api-endpoints';
import { Page } from '../models/pagination.model';

@Injectable({
  providedIn: 'root'
})
export class OrderService {

  constructor(private http: HttpClient) { }

  getStatistics(start: string, end: string): Observable<any> {
    return this.http.get<any>(`order:${API_ENDPOINTS.ORDERS.STATISTICS(start, end)}`);
  }

  createOrder(order: Partial<Order>): Observable<Order> {
    return this.http.post<Order>(`order:${API_ENDPOINTS.ORDERS.CREATE}`, order);
  }

  getOrder(id: string | number): Observable<Order> {
    return this.http.get<Order>(`order:${API_ENDPOINTS.ORDERS.DETAIL(id)}`);
  }

  getOrders(page: number = 0, size: number = 20, status?: string): Observable<Page<Order>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    if (status) params = params.set('status', status);
    return this.http.get<Page<Order>>(`order:${API_ENDPOINTS.ORDERS.LIST}`, { params });
  }

  getAllOrders(): Observable<Order[]> {
    return this.getOrders(0, 100).pipe(map(response => response.content || []));
  }

  getOrdersByStatus(status: string): Observable<Order[]> {
    return this.http.get<Order[]>(`order:${API_ENDPOINTS.ORDERS.BY_STATUS(status)}`);
  }

  updateOrder(id: string | number, order: Partial<Order>): Observable<Order> {
    return this.http.put<Order>(`order:${API_ENDPOINTS.ORDERS.DETAIL(id)}`, order);
  }

  updateOrderStatus(id: string | number, status: string): Observable<Order> {
    return this.http.put<Order>(
      `order:${API_ENDPOINTS.ORDERS.STATUS(id)}?status=${encodeURIComponent(status)}`,
      {}
    );
  }

  cancelOrder(id: string | number): Observable<Order> {
    return this.http.put<Order>(`order:${API_ENDPOINTS.ORDERS.CANCEL(id)}`, null);
  }

  deleteOrder(id: string | number): Observable<void> {
    return this.http.delete<void>(`order:${API_ENDPOINTS.ORDERS.DETAIL(id)}`);
  }

  getReadyForPickupOrders(): Observable<Order[]> {
    return this.http.get<Order[]>(`order:${API_ENDPOINTS.ORDERS.READY_FOR_PICKUP}`);
  }

  getOverdueOrders(): Observable<Order[]> {
    return this.http.get<Order[]>(`order:${API_ENDPOINTS.ORDERS.OVERDUE}`);
  }

  getOrdersByTable(tableNumber: string): Observable<Order[]> {
    return this.http.get<Order[]>(`order:${API_ENDPOINTS.ORDERS.BY_TABLE(tableNumber)}`);
  }

  getOrdersByCustomer(customerName: string): Observable<Order[]> {
    return this.http.get<Order[]>(`order:${API_ENDPOINTS.ORDERS.BY_CUSTOMER(customerName)}`);
  }

  getOrderByInvoice(invoiceNumber: string): Observable<Order> {
    return this.http.get<Order>(`order:${API_ENDPOINTS.ORDERS.BY_INVOICE(invoiceNumber)}`);
  }

  searchOrders(filters: {
    invoiceNumber?: string;
    tableNumber?: string;
    customerName?: string;
    status?: string;
    paymentMethod?: string;
    page?: number;
    size?: number;
  }): Observable<Page<Order>> {
    let params = new HttpParams();
    if (filters.invoiceNumber) params = params.set('invoiceNumber', filters.invoiceNumber);
    if (filters.tableNumber) params = params.set('tableNumber', filters.tableNumber);
    if (filters.customerName) params = params.set('customerName', filters.customerName);
    if (filters.status) params = params.set('status', filters.status);
    if (filters.paymentMethod) params = params.set('paymentMethod', filters.paymentMethod);
    params = params.set('page', (filters.page ?? 0).toString());
    params = params.set('size', (filters.size ?? 20).toString());
    return this.http.get<Page<Order>>(`order:${API_ENDPOINTS.ORDERS.SEARCH}`, { params });
  }
}
